import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Database, TriangleAlert } from "lucide-react";

function MetaFooter({ meta }) {
  if (!meta) return null;
  return (
    <div className="mt-3 pt-2 border-t border-border/50 space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {(meta.sources || []).map((s) => (
          <span key={s} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-background border border-border rounded-sm font-mono text-[9px] text-muted-foreground">
            <Database className="h-2.5 w-2.5" /> {s}
          </span>
        ))}
        <span className={`inline-flex items-center px-1.5 py-0.5 border rounded-sm font-mono text-[9px] uppercase ${meta.degraded ? "text-red-400 border-red-400/40" : "text-[#FFB800] border-[#FFB800]/40"}`}>
          confidence: {meta.confidence}
        </span>
      </div>
      <p className="text-[9px] text-muted-foreground leading-snug">{meta.disclaimer}</p>
    </div>
  );
}

export default function CopilotTab() {
  const [messages, setMessages] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/susfin/copilot/messages")
      .then((r) => { setMessages(r.data.messages); setPrompts(r.data.suggested_prompts || []); })
      .catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text || "").trim();
    if (!msg || busy) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...(m || []), { id: `tmp-${Date.now()}`, role: "user", content: msg }]);
    try {
      const { data } = await api.post("/susfin/copilot/chat", { message: msg });
      setMessages((m) => [...m, data]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const clear = async () => {
    try {
      await api.delete("/susfin/copilot/messages");
      setMessages([]);
      toast.success("Conversation cleared");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!messages) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading Funding Copilot…</div>;

  return (
    <div className="card-flat flex flex-col" style={{ height: "calc(100vh - 320px)", minHeight: 480 }} data-testid="sf-copilot">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-accent flex items-center justify-center rounded-sm shrink-0">
            <Sparkles className="h-4 w-4 text-black" />
          </div>
          <div>
            <div className="font-display font-semibold leading-tight">AI Funding Copilot</div>
            <div className="text-[10px] text-muted-foreground">Grounded in your register, cases, matches, scores, applications, targets & calendar</div>
          </div>
        </div>
        {messages.length > 0 && (
          <Button size="sm" variant="outline" onClick={clear} data-testid="sf-copilot-clear">
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4" data-testid="sf-copilot-messages">
        {messages.length === 0 && (
          <div className="max-w-2xl" data-testid="sf-copilot-empty">
            <p className="text-sm text-muted-foreground mb-4">
              Ask anything about funding your sustainability projects — answers use only your recorded data and
              always flag what's missing. Try one of these:
            </p>
            <div className="flex flex-wrap gap-2">
              {prompts.map((p, idx) => (
                <button key={idx} onClick={() => send(p)}
                  className="text-left text-xs border border-border rounded-sm px-3 py-2 hover:border-accent hover:text-accent transition-colors"
                  data-testid={`sf-copilot-chip-${idx}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={m.id || idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-sm px-4 py-3 text-sm ${m.role === "user" ? "bg-accent text-accent-foreground" : "bg-surface border border-border"}`}
              data-testid={`sf-copilot-msg-${m.role}-${idx}`}>
              <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              {m.role === "assistant" && <MetaFooter meta={m.meta} />}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="sf-copilot-thinking">
            <div className="bg-surface border border-border rounded-sm px-4 py-3 text-sm text-muted-foreground font-mono animate-pulse">
              Analysing your funding data…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length > 0 && !busy && (
        <div className="px-5 pb-2 flex flex-wrap gap-1.5">
          {prompts.slice(0, 3).map((p, idx) => (
            <button key={idx} onClick={() => send(p)}
              className="text-[10px] border border-border/70 rounded-sm px-2 py-1 text-muted-foreground hover:border-accent hover:text-accent transition-colors"
              data-testid={`sf-copilot-quickchip-${idx}`}>
              {p}
            </button>
          ))}
        </div>
      )}

      <div className="p-4 border-t border-border flex items-end gap-2">
        <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder='e.g. "How do I fund the chiller plant optimisation?"'
          className="resize-none" data-testid="sf-copilot-input" />
        <Button variant="primary" onClick={() => send(input)} disabled={busy || !input.trim()} data-testid="sf-copilot-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
      <div className="px-4 pb-3 -mt-2 flex items-center gap-1 text-[9px] text-muted-foreground">
        <TriangleAlert className="h-2.5 w-2.5" /> Final eligibility and financing decisions remain subject to the relevant agency or financial institution.
      </div>
    </div>
  );
}
